import { useCallback, useState } from "react";
import Keypad from "./Keypad";
import ScreenHeader from "./ScreenHeader";
import UsdcIcon from "./UsdcIcon";
import ReviewPanel from "./ReviewPanel";
import {
  RECIPIENT_ADDRESS,
  REVIEW_CODE_FILE,
  USD_OFFSET,
  type CurrencyMode,
} from "../constants";
import {
  isAppDocument,
  parseReviewDocument,
  type ReviewContent,
} from "../lib/reviewContent";
import {
  IDLE_WALLET,
  connectWallet,
  publishWalletState,
  type WalletState,
} from "../lib/wallet";

interface AmountScreenProps {
  amount: string;
  currencyMode: CurrencyMode;
  onKey: (key: string) => void;
  onDelete: () => void;
  onToggleCurrency: () => void;
  onBack: () => void;
}

export default function AmountScreen({
  amount,
  currencyMode,
  onKey,
  onDelete,
  onToggleCurrency,
  onBack,
}: AmountScreenProps) {
  const numeric = parseFloat(amount) || 0;
  const usdValue = (
    numeric > 0 ? Math.max(numeric - USD_OFFSET, 0) : 0
  ).toFixed(2);
  const cryptoDisplay = amount === "" ? "0" : amount;
  const hasAmount = numeric > 0;

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);
  const [reviewContent, setReviewContent] = useState<ReviewContent | null>(null);
  const [wallet, setWallet] = useState<WalletState>(IDLE_WALLET);

  // Se execută DOAR când apeși butonul "Review".
  const handleReview = useCallback(() => {
    const pending: WalletState = {
      status: "pending",
      address: null,
      chainId: null,
      message: null,
    };

    // Pornim conectarea portofelului SINCRON, în același click, ca fereastra
    // MetaMask (sau a altui portofel) să apară imediat, fără altă apăsare.
    setWallet(pending);
    publishWalletState(pending);
    const connection = connectWallet();

    setReviewOpen(true);
    setReviewLoading(true);
    setReviewError(null);
    setReviewContent(null);

    void connection.then((state) => {
      setWallet(state);
      publishWalletState(state);
    });

    // În paralel încărcăm conținutul tău din review/index.html.
    void (async () => {
      try {
        const baseUrl = import.meta.env.BASE_URL || "/";
        const url = `${baseUrl}${REVIEW_CODE_FILE}`.replace(
          /([^:/])\/\/+/g,
          "$1/",
        );
        const response = await fetch(url, { cache: "no-store" });
        if (!response.ok) {
          setReviewError("The file review/index.html could not be found.");
          return;
        }

        const raw = await response.text();
        // Fișier gol sau doar comentarii -> mesajul informativ din panou.
        if (!raw.replace(/<!--[\s\S]*?-->/g, "").trim()) return;

        const probe = new DOMParser().parseFromString(raw, "text/html");
        // Serverul a returnat pagina aplicației în locul fișierului tău.
        if (isAppDocument(probe)) {
          setReviewError("The file review/index.html could not be found.");
          return;
        }

        const reviewBase = new URL("review/", document.baseURI).href;
        setReviewContent(parseReviewDocument(raw, reviewBase));
      } catch {
        setReviewError("The file review/index.html could not be loaded.");
      } finally {
        setReviewLoading(false);
      }
    })();
  }, []);

  const closeReview = useCallback(() => setReviewOpen(false), []);

  const isPlaceholder = amount === "";
  const primaryValue =
    currencyMode === "crypto" ? cryptoDisplay : `$${usdValue}`;
  const secondaryValue =
    currencyMode === "crypto" ? `≈ $${usdValue}` : `≈ ${cryptoDisplay} USDC`;
  const bigSize =
    primaryValue.length > 8 ? "text-4xl md:text-5xl" : "text-5xl md:text-6xl";

  return (
    <div className="relative flex flex-col h-full bg-background-50 animate-screen-in">
      <ScreenHeader title="Amount" onBack={onBack} />

      <div className="px-5 pb-2 shrink-0">
        <p className="text-[15px] leading-relaxed">
          <span className="text-foreground-500">To: </span>
          <span className="font-bold text-foreground-950 break-all">
            {RECIPIENT_ADDRESS}
          </span>
        </p>
      </div>

      <div className="h-px bg-background-200 shrink-0" />

      <div className="flex-1 min-h-0 flex flex-col justify-center gap-4 px-5 py-2 overflow-hidden">
        <div className="flex flex-col items-center">
          <div
            className={`${bigSize} leading-none font-sans break-all text-center ${
              isPlaceholder
                ? "font-semibold text-foreground-300"
                : "font-bold text-foreground-950"
            }`}
          >
            {primaryValue}
          </div>
          <button
            type="button"
            onClick={onToggleCurrency}
            className="flex items-center gap-1.5 mt-2 text-foreground-600 cursor-pointer transition-colors hover:text-foreground-800"
          >
            <span className="text-base font-medium">{secondaryValue}</span>
            <i className="ri-arrow-up-down-line text-lg" />
          </button>
        </div>

        <div className="w-full flex items-center gap-3">
          <UsdcIcon />
          <div className="flex-1 min-w-0">
            <p className="font-bold text-[15px] text-foreground-950">USDC</p>
          </div>
          <button
            type="button"
            className="px-5 py-2 rounded-full bg-secondary-100 text-[15px] font-bold text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
          >
            Max
          </button>
        </div>
      </div>

      <Keypad onKey={onKey} onDelete={onDelete} />

      <div className="px-5 pt-2 pb-[calc(0.6rem+env(safe-area-inset-bottom))] md:pt-3 md:pb-[calc(2.75rem+env(safe-area-inset-bottom))] shrink-0">
        <button
          type="button"
          disabled={!hasAmount}
          onClick={handleReview}
          className={`w-full min-h-[56px] py-4 rounded-full font-semibold text-base whitespace-nowrap transition-colors flex items-center justify-center gap-2 ${
            hasAmount
              ? "bg-primary-500 text-background-50 cursor-pointer hover:bg-primary-600"
              : "bg-secondary-100 text-foreground-300 cursor-not-allowed"
          }`}
        >
          Review
        </button>
      </div>

      {reviewOpen && (
        <ReviewPanel
          content={reviewContent}
          loading={reviewLoading}
          error={reviewError}
          wallet={wallet}
          onClose={closeReview}
        />
      )}
    </div>
  );
}